import React, { useEffect } from "react";
import { ThemeName } from "../../util/themes";
import { getConfiguredThemeName } from "../../util/themes/ThemeProvider";

interface ThemeWrapperProps {
    children: React.ReactNode;
}

const LIQUID_GLASS_GLOBAL_CSS = `
  *, *::before, *::after {
    font-family: var(--font-family) !important;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  html, body {
    background-color: var(--cream-100);
    color: var(--ink-900);
    min-height: 100vh;
  }

  a {
    color: var(--ink-900);
    text-decoration: none;
    transition: opacity 0.2s ease;
  }
  a:hover { opacity: 0.72; }

  /* MUI overrides para tema azul */
  .MuiAppBar-root {
    background: var(--appbar-bg) !important;
    backdrop-filter: var(--blur-strong) !important;
    -webkit-backdrop-filter: var(--blur-strong) !important;
    border-bottom: 1px solid rgba(191,199,210,0.35) !important;
    box-shadow: 0 2px 20px rgba(0,58,92,0.06) !important;
    color: var(--ink-900) !important;
  }

  .MuiToolbar-root {
    background: transparent !important;
    color: var(--ink-900) !important;
  }

  .MuiPaper-root {
    background: var(--glass-bg);
    backdrop-filter: var(--blur-normal);
    -webkit-backdrop-filter: var(--blur-normal);
    border: 1px solid rgba(255,255,255,0.40);
    box-shadow: var(--shadow-md);
    color: var(--ink-900);
  }

  .MuiCard-root {
    background: var(--glass-bg);
    backdrop-filter: var(--blur-normal);
    -webkit-backdrop-filter: var(--blur-normal);
    border: 1px solid rgba(255,255,255,0.40);
    box-shadow: var(--shadow-md);
    border-radius: var(--radius-md) !important;
    color: var(--ink-900);
  }

  .MuiDrawer-paper {
    background: var(--cream-100) !important;
    border-right: 1px solid rgba(191,199,210,0.35);
    box-shadow: 0 24px 64px -16px rgba(0,58,92,0.18);
    color: var(--ink-900);
  }

  .MuiOutlinedInput-root {
    background: var(--glass-bg);
    backdrop-filter: blur(14px) saturate(160%);
    -webkit-backdrop-filter: blur(14px) saturate(160%);
    border-radius: var(--radius-sm) !important;
    color: var(--ink-900);
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: rgba(191,199,210,0.60) !important;
  }
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(191,199,210,0.90) !important;
  }
  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #00629c !important;
    border-width: 1.5px !important;
  }
  .MuiInputLabel-root { color: rgba(26,28,30,0.50); }
  .MuiInputLabel-root.Mui-focused { color: #00629c; }
  .MuiInputBase-input { color: var(--ink-900); }
  .MuiInputBase-input::placeholder { color: rgba(26,28,30,0.38); opacity: 1; }
  .MuiFormHelperText-root { color: rgba(26,28,30,0.55); }
  .MuiSelect-select { color: var(--ink-900); }

  .MuiButton-contained {
    background: var(--btn-dark-bg) !important;
    color: var(--btn-dark-text) !important;
    border-radius: 100px !important;
    box-shadow: none !important;
    font-weight: 600;
    letter-spacing: 0.02em;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .MuiButton-contained:hover {
    background: #004f80 !important;
    box-shadow: 0 8px 24px rgba(0,58,92,0.25) !important;
    transform: translateY(-1px);
  }
  .MuiButton-outlined {
    border-color: rgba(191,199,210,0.70) !important;
    color: var(--ink-900) !important;
    background: var(--glass-bg) !important;
    border-radius: 100px !important;
    font-weight: 500;
  }
  .MuiButton-outlined:hover {
    background: var(--surface-hover) !important;
    border-color: rgba(191,199,210,0.90) !important;
  }

  .MuiIconButton-root { color: var(--ink-900); }
  .MuiIconButton-root svg { color: var(--ink-900); fill: transparent; }

  .MuiChip-root {
    background: var(--glass-bg) !important;
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    border: 1px solid rgba(191,199,210,0.50) !important;
    border-radius: 100px !important;
    color: var(--ink-900) !important;
  }
  .MuiChip-filled {
    background: var(--btn-dark-bg) !important;
    color: var(--btn-dark-text) !important;
    border: none !important;
  }

  .MuiTypography-root { color: var(--ink-900); }

  .MuiDivider-root { border-color: rgba(191,199,210,0.50); }

  .MuiCheckbox-root { color: rgba(26,28,30,0.30); }
  .MuiCheckbox-root.Mui-checked { color: #00629c; }
  .MuiRadio-root { color: rgba(26,28,30,0.30); }
  .MuiRadio-root.Mui-checked { color: #00629c; }

  .MuiMenu-paper, .MuiPopover-paper {
    background: var(--menu-bg) !important;
    backdrop-filter: var(--blur-normal) !important;
    -webkit-backdrop-filter: var(--blur-normal) !important;
    border: 1px solid rgba(191,199,210,0.35) !important;
    border-radius: 20px !important;
    box-shadow: 0 16px 48px -12px rgba(0,58,92,0.14) !important;
  }
  .MuiMenuItem-root { color: var(--ink-900); border-radius: 10px; margin: 2px 6px; }
  .MuiMenuItem-root:hover { background: rgba(0,98,156,0.06); }
  .MuiMenuItem-root.Mui-selected { background: rgba(0,98,156,0.10); }

  .MuiBadge-badge {
    background: #00629c !important;
    color: #ffffff !important;
    font-size: 0.65rem;
    font-weight: 700;
  }

  .MuiDialog-paper {
    background: var(--glass-bg-strong) !important;
    backdrop-filter: var(--blur-strong) !important;
    -webkit-backdrop-filter: var(--blur-strong) !important;
    border: 1px solid rgba(255,255,255,0.60) !important;
    border-radius: var(--radius-lg) !important;
    box-shadow: 0 24px 64px -16px rgba(0,58,92,0.20) !important;
  }

  .MuiSnackbar-root .MuiPaper-root {
    border-radius: 20px !important;
    backdrop-filter: var(--blur-normal) !important;
  }

  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(0,58,92,0.15); border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(0,58,92,0.25); }
`;

export function ThemeWrapper({ children }: ThemeWrapperProps) {
    const themeName = getConfiguredThemeName();
    const isLiquidGlass = themeName === ThemeName.LIQUID_GLASS;

    useEffect(() => {
        const oldStyle = document.getElementById("theme-styles");
        if (oldStyle) oldStyle.remove();

        const style = document.createElement("style");
        style.id = "theme-styles";

        if (isLiquidGlass) {
            style.innerHTML = LIQUID_GLASS_GLOBAL_CSS;
        } else {
            style.innerHTML = `
        body, html {
          background-color: #ffffff;
          color: #010101;
          font-family: -apple-system, system-ui, sans-serif;
        }
        *, *::before, *::after { font-family: -apple-system, system-ui, sans-serif !important; }
      `;
        }

        document.head.appendChild(style);
        return () => {
            const s = document.getElementById("theme-styles");
            if (s) s.remove();
        };
    }, [themeName, isLiquidGlass]);

    return <>{children}</>;
}
